import dayjs from 'dayjs'
import { Dialog } from 'quasar'
import { getI18n } from '@/plugins/i18n'

import { exchangeRateSourceEnum, usePortalStore } from '@/stores/portal.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import { resolveCurrencyRef } from '@/composables/currency'

import CurrencyChangeDialog from '@/components/shared/CurrencyChangeDialog.vue'

export const EMPTY_EXCHANGE_RATE = Object.freeze({
  exchange_rate: null,
  exchange_rate_date: null,
  exchange_rate_source: null,
  exchange_rate_base_currency_ref: null,
})

export const pickExchangeRate = (item) => Object.fromEntries(Object.keys(EMPTY_EXCHANGE_RATE).map(field => [field, item?.[field] ?? null]))

export const isReferenceCurrency = (currencyRef) => resolveCurrencyRef(currencyRef) === resolveCurrencyRef(null)

export const currencyCode = (currencyRef) => useWebSiteStore().currencies.find(c => c.id === resolveCurrencyRef(currencyRef))?.code

export const exchangeRateSource = (source) => usePortalStore().getLocalizedExchangeRateSources().find(s => s.id === source)

export const parseExchangeRate = (value) => Number(value) || null

export const formatExchangeRate = (rate) => Number(rate).toLocaleString(getI18n().locale.value, { maximumFractionDigits: 6 })

export const isSameExchangeRate = (a, b) =>
  Number(a?.exchange_rate ?? 0) === Number(b?.exchange_rate ?? 0) &&
  (a?.exchange_rate_date ?? null) === (b?.exchange_rate_date ?? null) &&
  (a?.exchange_rate_source ?? null) === (b?.exchange_rate_source ?? null)

const tableExchangeRate = (currencyRef, prefix, source) => {
  const currency = useWebSiteStore().currencies.find(c => c.id === currencyRef)
  return currency?.[`${prefix}_exchange_rate`] == null ? null : {
    exchange_rate: Number(currency[`${prefix}_exchange_rate`]),
    exchange_rate_date: currency[`${prefix}_exchange_rate_date`],
    exchange_rate_source: source ?? currency[`${prefix}_exchange_rate_source`],
    exchange_rate_base_currency_ref: resolveCurrencyRef(null),
  }
}

export const ecbExchangeRate = (currencyRef) => tableExchangeRate(currencyRef, 'ecb')

export const manualTableExchangeRate = (currencyRef) => tableExchangeRate(currencyRef, 'manual', exchangeRateSourceEnum.MANUAL)

const effectiveTableExchangeRate = (currencyRef) => manualTableExchangeRate(currencyRef) ?? ecbExchangeRate(currencyRef)

export const getTableExchangeRate = (currencyRef) =>
  isReferenceCurrency(currencyRef) ? 1 : effectiveTableExchangeRate(currencyRef)?.exchange_rate ?? null

export const convertWithRates = (amount, fromRate, toRate) => fromRate && toRate ? amount / fromRate * toRate : null

export const documentExchangeRate = (document) => ({
  currencyRef: document?.currency_ref,
  rate: document?.exchange_rate != null ? Number(document.exchange_rate) : null,
  baseRef: document?.exchange_rate_base_currency_ref,
})

const getExchangeRate = (currencyRef, documentRate) => {
  if (documentRate?.rate == null || documentRate.currencyRef !== currencyRef) {
    return getTableExchangeRate(currencyRef)
  }
  const baseRate = getTableExchangeRate(resolveCurrencyRef(documentRate.baseRef))
  return baseRate ? documentRate.rate * baseRate : null
}

/** Rates are per 1 unit of the reference currency; returns null when a rate is missing. */
export const convertCurrencyAmount = (amount, fromRef, toRef, documentRate = null) => {
  const from = resolveCurrencyRef(fromRef)
  const to = resolveCurrencyRef(toRef)
  if (!amount || from === to) {
    return amount
  }
  return convertWithRates(amount, getExchangeRate(from, documentRate), getExchangeRate(to, documentRate))
}

export const referenceCurrencyHint = (currencyRef) =>
  currencyRef != null && isReferenceCurrency(currencyRef) ? getI18n().t('company.exchangeRates.referenceCurrency') : undefined

export const manualExchangeRate = (rate, current) => ({
  exchange_rate: rate,
  exchange_rate_date: current?.exchange_rate_source === exchangeRateSourceEnum.MANUAL && current.exchange_rate_date ? current.exchange_rate_date : dayjs().format('YYYY-MM-DD'),
  exchange_rate_source: exchangeRateSourceEnum.MANUAL,
  exchange_rate_base_currency_ref: current?.exchange_rate_base_currency_ref ?? resolveCurrencyRef(null),
})

export const exchangeRateForCurrency = (currencyRef, saved) => {
  if (isReferenceCurrency(currencyRef)) return { ...EMPTY_EXCHANGE_RATE }
  if (saved?.currency_ref === currencyRef && saved.exchange_rate != null) return pickExchangeRate(saved)
  return effectiveTableExchangeRate(currencyRef) ?? { ...EMPTY_EXCHANGE_RATE }
}

export const isRepriceChoiceNeeded = (saved, edited) =>
  !!saved?.id &&
  saved.exchange_rate != null &&
  edited.exchange_rate != null &&
  saved.currency_ref === edited.currency_ref &&
  Number(saved.exchange_rate) !== Number(edited.exchange_rate)

/** Resolves to `reprice_rows` (true / false), or null when cancelled. */
export const askRepriceRows = () => new Promise((resolve) => {
  const i18n = getI18n()
  Dialog.create({
    title: i18n.t('company.exchangeRates.rateChangedTitle'),
    message: i18n.t('company.exchangeRates.rateChangedMessage'),
    options: {
      type: 'radio',
      model: false,
      items: [
        { label: i18n.t('company.exchangeRates.keepPrices'), value: false },
        { label: i18n.t('company.exchangeRates.repricePrices'), value: true },
      ],
    },
    ok: { label: i18n.t('common.save'), color: 'positive' },
    cancel: { label: i18n.t('common.cancel'), flat: true },
    persistent: true,
  })
    .onOk(resolve)
    .onCancel(() => resolve(null))
})

/** Resolves to the amounts to apply (converted or unchanged), or null when cancelled. */
export const promptCurrencyChange = (fromRef, toRef, amounts) => new Promise((resolve) => {
  const hasAmounts = Object.values(amounts).some(amount => amount != null && amount !== '' && Number(amount) !== 0)
  if (!fromRef || !toRef || fromRef === toRef || !hasAmounts) {
    resolve(amounts)
    return
  }
  Dialog.create({ component: CurrencyChangeDialog, componentProps: { fromRef, toRef, amounts } })
    .onOk(resolve)
    .onCancel(() => resolve(null))
})

export const useExchangeRateEdit = (editItem, saved) => {
  const onCurrencyChange = (currencyRef) => {
    editItem.value.currency_ref = currencyRef
    Object.assign(editItem.value, exchangeRateForCurrency(currencyRef, saved.value))
  }

  const onExchangeRateChange = (values) => Object.assign(editItem.value, values)

  /** Resolves to `reprice_rows` (true / false), or null when cancelled. */
  const confirmRepriceRowsAsync = async() => isRepriceChoiceNeeded(saved.value, editItem.value) ? askRepriceRows() : false

  return { onCurrencyChange, onExchangeRateChange, confirmRepriceRowsAsync }
}
